import {
  DefaultApi as AppApi,
  Configuration as AppConfiguration,
  ConfigurationParameters
} from '@/types/app';
import {
  DefaultApi as PhoneAgentApi,
  Configuration as PhoneAgentConfiguration
} from '@/types/crm-phone-agent';
import {
  DefaultApi as CrmScreenerApi,
  Configuration as CrmScreenerApiConfiguration
} from '@/types/crm-screener';
import {
  DefaultApi as CrmScreenerApiApi,
  Configuration as CrmScreenerApiApiConfiguration
} from '@/types/crm-screener-api';
import {
  DefaultApi as CrmScreenerDocumentsApi,
  Configuration as CrmScreenerDocumentsConfiguration
} from '@/types/crm-screener-documents';
import {
  DefaultApi as CrmScreenerInsurancesApi,
  Configuration as CrmScreenerInsurancesConfiguration
} from '@/types/crm-screener-insurances';
import {
  DefaultApi as CrmScreenerIntakeApi,
  Configuration as CrmScreenerIntakeConfiguration
} from '@/types/crm-screener-intake';
import {
  DefaultApi as DataCenterApi,
  Configuration as DataCenterConfiguration
} from '@/types/data-center';
import {
  DefaultApi as HomeHealthApi,
  Configuration as HomeHealthApiConfiguration
} from '@/types/home-health-api';
import {
  DefaultApi as NotificationsApi,
  Configuration as NotificationsConfiguration
} from '@/types/notifications';
import {
  DefaultApi as ReferralNetworkApi,
  Configuration as ReferralNetworkConfiguration
} from '@/types/referral-network';
import {
  DefaultApi as RocApi,
  Configuration as RocConfiguration
} from '@/types/roc';
import {
  DefaultApi as UsersApi,
  Configuration as UsersConfiguration
} from '@/types/users';

import { getAuthAccessToken, getAuthorizationHeader } from './authAccessToken';
import { createGlobalErrorHandler } from './globalErrorHandler';
import { newVersionMiddleware } from './newVersionMiddleware';

function setupConfigurationParams(basePath: string): ConfigurationParameters {
  return {
    basePath,
    middleware: [
      {
        pre: async (context) => {
          const accessToken = await getAuthAccessToken();
          context.init.headers = {
            ...context.init.headers,
            Authorization: getAuthorizationHeader(accessToken),
            'x-fe-page': window.location.href
          };
          return context;
        },
        post: async (context) => {
          const globalHandler = createGlobalErrorHandler();
          const response = await globalHandler.post(context);

          return newVersionMiddleware(response);
        }
      }
    ]
  };
}

export const appClient = new AppApi(
  new AppConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_APP)
  )
);

export const dataCenterClient = new DataCenterApi(
  new DataCenterConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_DATA_CENTER)
  )
);

export const phoneAgentClient = new PhoneAgentApi(
  new PhoneAgentConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_PHONE_AGENT)
  )
);

export const notificationsClient = new NotificationsApi(
  new NotificationsConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_NOTIFICATIONS)
  )
);

export const usersv2Client = new UsersApi(
  new UsersConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_USERS)
  )
);

export const crmScreenerClient = new CrmScreenerApi(
  new CrmScreenerApiConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_CRM_SCREENER)
  )
);

export const crmScreenerApiClient = new CrmScreenerApiApi(
  new CrmScreenerApiApiConfiguration(
    setupConfigurationParams(
      import.meta.env.VITE_API_BASE_PATH_CRM_SCREENER_API
    )
  )
);

export const crmScreenerDocumentsClient = new CrmScreenerDocumentsApi(
  new CrmScreenerDocumentsConfiguration(
    setupConfigurationParams(
      import.meta.env.VITE_API_BASE_PATH_CRM_SCREENER_DOCUMENTS
    )
  )
);

export const crmScreenerInsurancesClient = new CrmScreenerInsurancesApi(
  new CrmScreenerInsurancesConfiguration(
    setupConfigurationParams(
      import.meta.env.VITE_API_BASE_PATH_CRM_SCREENER_INSURANCES
    )
  )
);

export const palReauthClient = crmScreenerInsurancesClient;

export const palReauthCaseManagersClient = crmScreenerInsurancesClient;

export const crmScreenerIntakeClient = new CrmScreenerIntakeApi(
  new CrmScreenerIntakeConfiguration(
    setupConfigurationParams(
      import.meta.env.VITE_API_BASE_PATH_CRM_SCREENER_INTAKE
    )
  )
);

export const homeHealthApiClient = new HomeHealthApi(
  new HomeHealthApiConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_HOME_HEALTH_API)
  )
);

export const referralNetworkClient = new ReferralNetworkApi(
  new ReferralNetworkConfiguration(
    setupConfigurationParams(
      import.meta.env.VITE_API_BASE_PATH_REFERRAL_NETWORK
    )
  )
);

export const rocClient = new RocApi(
  new RocConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_ROC)
  )
);
